import React, { useEffect, useMemo, useState } from 'react'
import PublicSiteShell from '../components/v2/PublicSiteShell'
import '../styles/chatter-score.css'

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api'

const SAMPLE_TRANSCRIPT = `Fan: hey
Chatter: hey, glad you came back :)
Fan: do you have another video like that? how much?
Chatter: haha what are you doing tonight?
Fan: I really like talking to you, I'm just broke until payday lol
Chatter: okay`

const SAMPLE_RESULT = {
  overall_score: 54,
  diagnosis: 'Buying intent detected, but the conversation abandoned both the immediate offer and the future return path.',
  summary: 'The chatter stayed friendly, but changed the subject after a direct product-and-price question and then ended the thread after a temporary budget objection.',
  normalized_message_count: 6,
  privacy: { raw_transcript_retained: false },
  scores: {
    buy_signal_capture: 35,
    offer_timing: 42,
    nurture_and_ltv: 48,
    objection_recovery: 30,
    relationship_progression: 64,
    sales_pressure_control: 92,
    psychological_attunement: 72,
  },
  revenue_strategy_balance: {
    purchase_observed: false,
    immediate_session_execution_score: 36,
    long_term_ltv_protection_score: 66,
    label: 'Relationship value under-monetized',
    explanation: 'The fan revealed both product interest and a reason to return, but neither was converted into a useful next step.',
    principle: 'A purchase is an outcome signal, not proof that the chatter used the best strategy for the fan’s total future value.',
  },
  v4_profile: {
    version: 'chatter-score-personal-experience-v4.1',
    disclaimer: 'These are evidence-based behavioural hypotheses, not diagnoses or purchase probabilities.',
    coverage: { fan_turns: 3, turns_with_evidence: 2, coverage_percent: 67, stable_signals: 0, emerging_signals: 2 },
    relationship_dynamics: [
      { signal: 'closeness_seeking', label: 'Closeness seeking', support_balance: 100, confidence: 24, stable: false, status: 'emerging', supporting_evidence_count: 1, independent_evidence_count: 1, message_indices: [5] },
    ],
    value_drivers: [
      { signal: 'novelty_and_collection', label: 'Novelty and collection', support_balance: 100, confidence: 35, stable: false, status: 'emerging', supporting_evidence_count: 1, independent_evidence_count: 1, message_indices: [3] },
    ],
    behavioural_archetypes: [
      { key: 'collector', label: 'Content collector', strength: 35, status: 'emerging', description: 'Values new items or more content in a known category.', recommended_approach: 'Offer relevant catalog choices without forcing a personal narrative.', message_indices: [3] },
      { key: 'closeness_seeker', label: 'Closeness seeker', strength: 24, status: 'emerging', description: 'Values reliable warmth, continuity, and feeling remembered.', recommended_approach: 'Use consistent warmth and specific callbacks; do not manufacture abandonment anxiety.', message_indices: [5] },
    ],
    recommended_relationship_strategy: { strategy: 'CONSISTENT_WARMTH', reason: 'Strongest observed relationship signal: Closeness seeking.', constraints: ['Use remembered details and consistent warmth.', 'Do not manufacture jealousy, disappearance, or abandonment anxiety.'] },
    product_fit_hypotheses: [
      { key: 'catalog_collection', label: 'Catalog PPV / collection', strength: 35, status: 'discovery', evidence_count: 1, next_step: 'Match a ready item or collection to the stated preference and answer price questions directly.', eligibility: 'UNKNOWN', availability: 'UNKNOWN' },
    ],
  },
  llm_report: {
    version: 'chatter-score-multicall-v1',
    status: 'complete',
    analysis_layers_completed: 3,
    commercial_review: {
      purchase_observed: false,
      ltv_impact: {
        risk: 'medium',
        confidence: 84,
        evidence_message_indices: [5, 6],
        reason: 'The temporary budget objection was followed by a cold close, which weakened a clear future return path.',
      },
      higher_value_opportunity: {
        status: 'plausible',
        type: 'better_product_fit',
        confidence: 66,
        evidence_message_indices: [3, 5],
        reason: 'The fan asked for a related video and also valued the conversation. Clarifying the desired intensity and timing could have revealed a better-fit offer.',
        missing_evidence: ['Accepted budget', 'Preferred format', 'Previous purchase history'],
        disclaimer: 'This is a counterfactual opportunity assessment, not proof that a higher price or different product would have converted.',
      },
      session_vs_ltv: 'ltv_under_monetized',
    },
    narrative: {
      executive_summary: 'The chatter preserved a friendly tone, but missed both the direct buying question and the chance to convert a temporary budget constraint into a specific future path.',
      what_went_well: ['The opening remained warm and low-pressure.', 'The chatter did not push another offer after the budget objection.'],
      immediate_revenue_analysis: 'The direct request for similar content and price was the clearest immediate sales moment. It was not advanced.',
      future_ltv_analysis: 'The fan explicitly said he liked the conversation and named payday as a timing boundary. Ending with “okay” failed to preserve that signal.',
      higher_value_opportunity_analysis: 'A better-fit or higher-value route is plausible, but the transcript does not prove that a higher price would have converted. Preference and budget clarification were missing.',
      recommended_strategy: ['Answer the product question directly.', 'Clarify desired intensity or format.', 'Keep a low-pressure payday follow-up path.'],
      key_moments: [
        { message_index: 3, title: 'Direct buying intent', analysis: 'The fan asked for another similar video and its price; the reply changed the subject.' },
        { message_index: 5, title: 'Future purchase timing', analysis: '“Until payday” created a future hook, not a permanent rejection.' },
      ],
    },
    disclaimer: 'The LLM explains evidence but cannot change Core scores or findings. Higher-value alternatives are counterfactual assessments, not guaranteed revenue.',
  },
  revenue_leak_summary: {
    immediate_revenue_leaks: 1,
    future_ltv_leaks: 1,
    by_horizon: { immediate: 1, short_term: 0, long_term: 1, retention: 0 },
  },
  findings: [
    {
      message_index: 3,
      type: 'MISSED_BUY_SIGNAL',
      severity: 'high',
      reason: 'The fan asked for similar content and its price. The next reply changed the subject instead of clarifying fit, contents, or budget.',
      revenue_horizon: 'immediate',
      revenue_impact: 'High',
      actual_action: 'Changed subject',
      recommended_action: 'Clarify and offer',
      fan_state_before: { engagement_score: 72, emotional_attachment_score: 36, nurture_potential_score: 70, immediate_purchase_readiness: 86 },
    },
    {
      message_index: 5,
      type: 'PREMATURE_ABANDONMENT',
      severity: 'medium',
      reason: '“Broke until payday” is a timing constraint, not a permanent rejection. The thread needed warmth plus a specific, non-pushy return point.',
      revenue_horizon: 'long_term',
      revenue_impact: 'Medium',
      actual_action: 'Ended conversation',
      recommended_action: 'Preserve future intent',
      fan_state_before: { engagement_score: 78, emotional_attachment_score: 58, nurture_potential_score: 88, immediate_purchase_readiness: 32 },
    },
  ],
  recommendations: [
    {
      finding_type: 'MISSED_BUY_SIGNAL',
      message_index: 3,
      better_strategy: 'Answer the product question before returning to rapport.',
      what_happened: 'The fan supplied category and price intent. A short clarifying question would keep the sale moving without pressure.',
      example_response: 'I do. Do you want something playful like the last one or a little bolder? I can show you the best fit and the price.',
    },
    {
      finding_type: 'PREMATURE_ABANDONMENT',
      message_index: 5,
      better_strategy: 'Keep the relationship warm and retain the payday signal.',
      what_happened: 'The fan still expressed liking the conversation. The right move is to acknowledge that value and remove immediate pressure.',
      example_response: 'No pressure tonight. I like talking to you too. Tell me when payday stops being the villain and I will remember what you wanted.',
    },
  ],
}

const SCORE_LABELS = {
  buy_signal_capture: 'Buy Signal Detection',
  offer_timing: 'Offer Timing',
  nurture_and_ltv: 'Long-Term LTV',
  objection_recovery: 'Objection Recovery',
  relationship_progression: 'Relationship Building',
  retention: 'Retention',
  personalization: 'Personalization',
  sales_pressure_control: 'Sales Pressure Control',
  reengagement: 'Reengagement',
  conversation_quality: 'Conversation Quality',
  psychological_attunement: 'Psychological Attunement',
}

const LEAK_LABELS = {
  MISSED_BUY_SIGNAL: 'Missed buying signal',
  PREMATURE_OFFER: 'Premature offer',
  OVERSELLING: 'Overselling',
  FAILED_OFFER_RECOVERY: 'Failed offer recovery',
  PREMATURE_ABANDONMENT: 'High-potential fan abandoned',
  NON_BUYER_NEGLECT: 'Non-buyer neglect',
  MISSED_NURTURE: 'Missed nurture',
  EXCESSIVE_PRESSURE: 'Excessive pressure',
  OFFER_COOLDOWN_VIOLATION: 'Offer cooldown violation',
  MISSED_VALUE_SIGNAL: 'Missed value signal',
  VULNERABILITY_PRESSURE: 'Relationship signal used as pressure',
  AUTONOMY_MISMATCH: 'Autonomy mismatch',
  RELATIONSHIP_REGRESSION: 'Relationship regression',
  SESSION_REVENUE_BIAS: 'Session revenue over fan LTV',
}

const HORIZON_LABELS = {
  immediate: 'Immediate',
  short_term: 'Short-term',
  long_term: 'Future LTV',
  retention: 'Retention',
}

const DEFAULT_METADATA = {
  fan_blocked: false,
  conversation_abandoned: false,
  chatter_stopped_reply: false,
  purchase_happened: false,
  retain_raw_transcript: false,
}

const ANALYSIS_STAGES = [
  { after: 0, label: 'Parsing speakers and normalizing the transcript', detail: 'Separating Fan and Chatter turns in a short-lived processing context.' },
  { after: 4, label: 'Reconstructing fan state and value signals', detail: 'Reviewing intent, resistance, relationship dynamics, and product-fit evidence.' },
  { after: 12, label: 'Comparing immediate revenue with future LTV', detail: 'Independent reviewers are checking sales execution and relationship cost.' },
  { after: 25, label: 'Testing higher-value alternatives', detail: 'Checking whether a better product or price path is supported, plausible, or unproven.' },
  { after: 40, label: 'Validating and writing the final report', detail: 'Core is checking evidence references and removing unsupported claims.' },
]

const AUDIT_POLL_INTERVAL_MS = 1500
const AUDIT_POLL_ATTEMPTS = 200

function wait(milliseconds) {
  return new Promise((resolve) => window.setTimeout(resolve, milliseconds))
}

async function pollAudit(auditId) {
  let consecutiveFailures = 0
  for (let attempt = 0; attempt < AUDIT_POLL_ATTEMPTS; attempt += 1) {
    await wait(AUDIT_POLL_INTERVAL_MS)
    try {
      const response = await fetch(`${API_BASE}/chatter-audit/${auditId}`, {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.detail || 'Could not load the audit result')
      }
      consecutiveFailures = 0
      if (data.status === 'complete' || data.status === 'failed') return data
    } catch (error) {
      consecutiveFailures += 1
      if (consecutiveFailures >= 3) throw error
    }
  }
  throw new Error('The deep report is still processing. Please retry this audit shortly.')
}

function parsePreview(raw, swapRoles) {
  const labels = []
  raw.split(/\r?\n/).forEach((line) => {
    const match = line.trim().match(/^([^:]{2,32}):\s+(.+)$/)
    if (match) labels.push(match[1].trim())
  })
  const unique = Array.from(new Set(labels)).slice(0, 2)
  const fan = unique.find((label) => /fan|client|customer|subscriber|buyer|user/i.test(label)) || unique[0] || 'Left / first speaker'
  const chatter = unique.find((label) => /chatter|creator|model|seller|agent|operator/i.test(label)) || unique.find((label) => label !== fan) || 'Right / second speaker'

  return swapRoles
    ? { fan: chatter, chatter: fan, confidence: unique.length >= 2 ? 'medium' : 'low' }
    : { fan, chatter, confidence: unique.length >= 2 ? 'medium' : 'low' }
}

function scoreTone(value) {
  if (value >= 80) return 'good'
  if (value >= 60) return 'warn'
  return 'bad'
}

function scoreGrade(value) {
  if (value >= 85) return 'A'
  if (value >= 75) return 'B'
  if (value >= 65) return 'C'
  if (value >= 50) return 'D'
  return 'F'
}

function formatLeakType(type) {
  return LEAK_LABELS[type] || String(type || '').replaceAll('_', ' ')
}

function formatScoreKey(key) {
  return SCORE_LABELS[key] || String(key || '').replaceAll('_', ' ')
}

function findingAnchor(finding) {
  if (!finding?.message_index) return 'Conversation-level finding'
  return `Message ${finding.message_index}`
}

function stateValue(state, key) {
  const value = state?.[key]
  return Number.isFinite(Number(value)) ? Number(value) : 0
}

function MetadataCheckbox({ checked, label, hint, onChange, disabled = false }) {
  return (
    <label className="cs-checkbox">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>
        <strong>{label}</strong>
        {hint ? <em>{hint}</em> : null}
      </span>
    </label>
  )
}

function AnalysisProgress({ elapsedSeconds }) {
  const stageIndex = ANALYSIS_STAGES.reduce(
    (current, stage, index) => (elapsedSeconds >= stage.after ? index : current),
    0,
  )
  const stage = ANALYSIS_STAGES[stageIndex]

  return (
    <div className="cs-analysis-progress" role="status" aria-live="polite">
      <div className="cs-analysis-orbit" aria-hidden="true">
        <span className="cs-analysis-orbit__ring cs-analysis-orbit__ring--one" />
        <span className="cs-analysis-orbit__ring cs-analysis-orbit__ring--two" />
        <i />
      </div>
      <div className="cs-analysis-progress__copy">
        <div>
          <span>Deep audit in progress</span>
          <strong>{stage.label}</strong>
          <small>{stage.detail}</small>
        </div>
        <time>{elapsedSeconds}s</time>
        <div className="cs-analysis-track" aria-hidden="true"><span /></div>
        <div className="cs-analysis-dots" aria-hidden="true">
          {ANALYSIS_STAGES.map((item, index) => (
            <span
              className={index === stageIndex ? 'is-active' : index < stageIndex ? 'is-passed' : ''}
              key={item.label}
            />
          ))}
        </div>
      </div>
    </div>
  )
}

function FindingCard({ finding, compact = false }) {
  const state = finding.fan_state_before || {}
  return (
    <article className={`cs-finding-card ${compact ? 'cs-finding-card--compact' : ''}`}>
      <div className="cs-finding-card__head">
        <div>
          <p className="cs-eyebrow">{findingAnchor(finding)}</p>
          <h3>{formatLeakType(finding.type)}</h3>
        </div>
        <span className={`cs-severity cs-severity--${finding.severity}`}>{finding.severity}</span>
      </div>
      <p>{finding.reason}</p>
      <div className="cs-finding-meta">
        <span>{HORIZON_LABELS[finding.revenue_horizon] || finding.revenue_horizon}</span>
        <span>Impact: {finding.revenue_impact}</span>
        <span>{finding.actual_action} → {finding.recommended_action}</span>
      </div>
      {!compact ? (
        <div className="cs-state-grid">
          <span>
            <b>{stateValue(state, 'engagement_score')}</b>
            Engagement
          </span>
          <span>
            <b>{stateValue(state, 'emotional_attachment_score')}</b>
            Attachment
          </span>
          <span>
            <b>{stateValue(state, 'nurture_potential_score')}</b>
            Nurture Potential
          </span>
          <span>
            <b>{stateValue(state, 'immediate_purchase_readiness')}</b>
            Purchase Readiness
          </span>
        </div>
      ) : null}
    </article>
  )
}

function EvidenceSignalCard({ item }) {
  const messageIndices = item.message_indices || []
  return (
    <article className="cs-v4-signal-card">
      <div>
        <span className={`cs-v4-status cs-v4-status--${item.status || 'emerging'}`}>
          {item.status || 'emerging'}
        </span>
        <strong>{item.label || formatScoreKey(item.signal)}</strong>
      </div>
      <dl>
        <div><dt>Confidence</dt><dd>{item.confidence ?? 0}%</dd></div>
        <div><dt>Independent evidence</dt><dd>{item.independent_evidence_count || 0}</dd></div>
      </dl>
      <small>
        {messageIndices.length ? `Evidence at messages ${messageIndices.join(', ')}` : 'No source-addressable evidence'}
      </small>
    </article>
  )
}

function V4AuditPanel({ profile, balance }) {
  const relationship = profile?.relationship_dynamics || []
  const valueDrivers = profile?.value_drivers || []
  const archetypes = profile?.behavioural_archetypes || []
  const opportunities = profile?.product_fit_hypotheses || []
  const strategy = profile?.recommended_relationship_strategy || {}
  const coverage = profile?.coverage || {}
  const hasEvidence = relationship.length || valueDrivers.length

  return (
    <section className="cs-v4-panel" aria-label="Personal Experience V4 analysis">
      <div className="cs-section-head">
        <div>
          <p className="cs-eyebrow">Personal Experience V4</p>
          <h2>What this fan values — and whether the chatter used it well</h2>
        </div>
        <span className="cs-v4-version">{profile?.version || 'V4 evidence model'}</span>
      </div>

      {balance?.label ? (
        <div className="cs-strategy-balance">
          <div>
            <span>Immediate session execution</span>
            <strong>{balance.immediate_session_execution_score ?? '—'}</strong>
          </div>
          <div>
            <span>Long-term LTV protection</span>
            <strong>{balance.long_term_ltv_protection_score ?? '—'}</strong>
          </div>
          <div className="cs-strategy-balance__diagnosis">
            <span>Strategy balance</span>
            <strong>{balance.label}</strong>
            <small>{balance.explanation}</small>
            <small className="cs-strategy-principle">{balance.principle}</small>
          </div>
          {balance.purchase_observed ? (
            <p className="cs-purchase-warning">
              Purchase observed. The sale counts, but pressure and relationship damage still reduce the score.
            </p>
          ) : null}
        </div>
      ) : null}

      {hasEvidence ? (
        <>
          <div className="cs-v4-columns">
            <div>
              <span className="cs-v4-column-title">Relationship dynamics</span>
              <div className="cs-v4-signal-list">
                {relationship.slice(0, 4).map((item) => <EvidenceSignalCard item={item} key={item.signal} />)}
              </div>
            </div>
            <div>
              <span className="cs-v4-column-title">Value drivers</span>
              <div className="cs-v4-signal-list">
                {valueDrivers.slice(0, 4).map((item) => <EvidenceSignalCard item={item} key={item.signal} />)}
              </div>
            </div>
          </div>

          {archetypes.length ? (
            <div className="cs-v4-block">
              <span className="cs-v4-column-title">Behavioural archetypes</span>
              <div className="cs-archetype-grid">
                {archetypes.slice(0, 4).map((item) => (
                  <article key={item.key}>
                    <div><strong>{item.label}</strong><span>{item.status}</span></div>
                    <p>{item.description}</p>
                    <small>{item.recommended_approach}</small>
                  </article>
                ))}
              </div>
            </div>
          ) : null}

          <div className="cs-v4-columns cs-v4-columns--strategy">
            <div className="cs-v4-strategy">
              <span>Recommended relationship strategy</span>
              <strong>{String(strategy.strategy || 'OBSERVE_AND_CLARIFY').replaceAll('_', ' ')}</strong>
              <p>{strategy.reason}</p>
              {(strategy.constraints || []).map((constraint) => <small key={constraint}>{constraint}</small>)}
            </div>
            <div className="cs-v4-opportunities">
              <span>Product-fit hypotheses</span>
              {opportunities.length ? opportunities.slice(0, 4).map((item) => (
                <article key={item.key}>
                  <strong>{item.label}</strong>
                  <p>{item.next_step}</p>
                  <small>Fit: {item.status} · availability: {item.availability} · eligibility: {item.eligibility}</small>
                </article>
              )) : <p>No product fit inferred from the available evidence.</p>}
            </div>
          </div>
        </>
      ) : (
        <div className="cs-empty">
          Not enough repeated fan evidence to assign a behavioural pattern. The audit will not invent a psychotype from silence or one ambiguous line.
        </div>
      )}

      <div className="cs-v4-footer">
        <span>{coverage.turns_with_evidence || 0}/{coverage.fan_turns || 0} fan turns contained relevant evidence</span>
        <span>{coverage.stable_signals || 0} stable · {coverage.emerging_signals || 0} emerging signals</span>
        <small>{profile?.disclaimer}</small>
      </div>
    </section>
  )
}

function DeepAuditPanel({ report }) {
  if (!report || !Object.keys(report).length) return null
  const narrative = report.narrative || {}
  const commercial = report.commercial_review || {}
  const ltv = commercial.ltv_impact || {}
  const opportunity = commercial.higher_value_opportunity || {}
  const available = report.status === 'complete' || report.status === 'partial'
  const opportunityLabel = String(opportunity.status || 'insufficient_evidence').replaceAll('_', ' ')
  const opportunityType = String(opportunity.type || 'none').replaceAll('_', ' ')

  if (!available) {
    return (
      <section className="cs-deep-report cs-deep-report--unavailable">
        <div>
          <p className="cs-eyebrow">Deep multi-pass review</p>
          <h2>Core audit complete</h2>
        </div>
        <p>The evidence-based scores and findings above are complete. The optional narrative review is currently {String(report.status || 'unavailable').replaceAll('_', ' ')}.</p>
      </section>
    )
  }

  return (
    <section className="cs-deep-report" aria-label="Deep multi-pass LLM review">
      <div className="cs-section-head">
        <div>
          <p className="cs-eyebrow">Deep multi-pass review</p>
          <h2>Did this conversation maximize the sale — and protect the next one?</h2>
        </div>
        <span className={`cs-deep-status cs-deep-status--${report.status}`}>
          {report.analysis_layers_completed || 0}/3 layers
        </span>
      </div>

      <p className="cs-deep-summary">{narrative.executive_summary}</p>

      <div className="cs-deep-columns">
        <article>
          <span>Immediate revenue</span>
          <p>{narrative.immediate_revenue_analysis}</p>
        </article>
        <article>
          <span>Future LTV</span>
          <strong className={`cs-risk cs-risk--${ltv.risk || 'unknown'}`}>{ltv.risk || 'unknown'} risk</strong>
          <p>{narrative.future_ltv_analysis}</p>
          {(ltv.evidence_message_indices || []).length ? <small>Evidence: messages {ltv.evidence_message_indices.join(', ')}</small> : null}
        </article>
      </div>

      <article className={`cs-opportunity cs-opportunity--${opportunity.status || 'insufficient_evidence'}`}>
        <div>
          <span>Could a higher-value route have worked?</span>
          <strong>{opportunityLabel}</strong>
        </div>
        <p>{narrative.higher_value_opportunity_analysis || opportunity.reason}</p>
        <div className="cs-opportunity-meta">
          <span>Route: {opportunityType}</span>
          <span>Confidence: {opportunity.confidence || 0}%</span>
          {(opportunity.evidence_message_indices || []).length ? <span>Evidence: messages {opportunity.evidence_message_indices.join(', ')}</span> : null}
        </div>
        <small>{opportunity.disclaimer}</small>
      </article>

      {(narrative.what_went_well || []).length ? (
        <div className="cs-deep-list">
          <span>What the chatter did well</span>
          <ul>{narrative.what_went_well.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      ) : null}

      {(narrative.key_moments || []).length ? (
        <div className="cs-deep-moments">
          {narrative.key_moments.map((item) => (
            <article key={`${item.message_index}-${item.title}`}>
              <span>Message {item.message_index}</span>
              <strong>{item.title}</strong>
              <p>{item.analysis}</p>
            </article>
          ))}
        </div>
      ) : null}

      {(narrative.recommended_strategy || []).length ? (
        <div className="cs-deep-list">
          <span>Better commercial path</span>
          <ol>{narrative.recommended_strategy.map((item) => <li key={item}>{item}</li>)}</ol>
        </div>
      ) : null}

      <small className="cs-deep-disclaimer">{report.disclaimer}</small>
    </section>
  )
}

function ResultPanel({ result, sample = false }) {
  const scores = result?.scores || {}
  const leakSummary = result?.revenue_leak_summary || {}
  const horizons = leakSummary.by_horizon || {}
  const findings = result?.findings || []
  const recommendations = result?.recommendations || []

  return (
    <section className="cs-results" id={sample ? 'sample-report' : undefined} aria-label="ChatterScore results">
      {sample ? <div className="cs-sample-label"><strong>Finished sample report</strong><span>Demonstration analysis of the sample transcript above. No customer data is used.</span></div> : null}
      <div className="cs-score-hero">
        <div>
          <p className="cs-eyebrow">Your ChatterScore</p>
          <strong>{result.overall_score ?? '—'}<span>/100</span></strong>
          <p>{result.summary}</p>
        </div>
        <div className="cs-diagnosis">
          <span>Final diagnosis</span>
          <b>{result.diagnosis || '—'}</b>
        </div>
      </div>

      <div className="cs-split-metrics">
        <div>
          <span>Immediate Revenue Leaks</span>
          <strong>{leakSummary.immediate_revenue_leaks || 0}</strong>
          <small>{(horizons.immediate || 0) + (horizons.short_term || 0)} sales-timing leaks</small>
        </div>
        <div>
          <span>Future LTV Leaks</span>
          <strong>{leakSummary.future_ltv_leaks || 0}</strong>
          <small>{(horizons.long_term || 0) + (horizons.retention || 0)} nurture / retention leaks</small>
        </div>
        <div>
          <span>Messages analyzed</span>
          <strong>{result.normalized_message_count || 0}</strong>
          <small>Raw transcript retention: {result.privacy?.raw_transcript_retained ? 'enabled' : 'off'}</small>
        </div>
      </div>

      <div className="cs-score-grid">
        {Object.entries(scores).map(([key, value]) => (
          <div className={`cs-score-card cs-score-card--${scoreTone(value)}`} key={key}>
            <span>{formatScoreKey(key)}</span>
            <strong>{value}</strong>
            <small>Grade {scoreGrade(value)}</small>
          </div>
        ))}
      </div>

      <V4AuditPanel profile={result.v4_profile || {}} balance={result.revenue_strategy_balance || {}} />

      <DeepAuditPanel report={result.llm_report || {}} />

      <div className="cs-section-head">
        <div>
          <p className="cs-eyebrow">Critical findings</p>
          <h2>{findings.length ? `${findings.length} revenue leaks detected` : 'No major leaks detected'}</h2>
        </div>
      </div>

      <div className="cs-findings-list">
        {findings.slice(0, 3).map((finding) => (
          <FindingCard finding={finding} key={`${finding.type}-${finding.message_index}-${finding.actual_action}`} />
        ))}
        {!findings.length ? (
          <div className="cs-empty">
            This sample did not trigger MVP leak rules. Run a larger agency audit to compare teams and cohorts.
          </div>
        ) : null}
      </div>

      {recommendations.length ? (
        <div className="cs-ai-comparison">
          <p className="cs-eyebrow">What AI Sales Brain would do differently</p>
          <div className="cs-recommendations">
            {recommendations.slice(0, 3).map((recommendation) => (
              <article key={`${recommendation.finding_type}-${recommendation.message_index}`}>
                <span>{formatLeakType(recommendation.finding_type)}</span>
                <h3>{recommendation.better_strategy}</h3>
                <p>{recommendation.what_happened}</p>
                <blockquote>{recommendation.example_response}</blockquote>
              </article>
            ))}
          </div>
        </div>
      ) : null}
    </section>
  )
}

export default function ChatterScorePage() {
  const initialSample = typeof window !== 'undefined' && window.location.hash === '#sample-report'
  const [rawConversation, setRawConversation] = useState(initialSample ? SAMPLE_TRANSCRIPT : '')
  const [metadata, setMetadata] = useState(DEFAULT_METADATA)
  const [swapRoles, setSwapRoles] = useState(false)
  const [result, setResult] = useState(initialSample ? SAMPLE_RESULT : null)
  const [sampleMode, setSampleMode] = useState(initialSample)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [lead, setLead] = useState({
    email: '',
    company_type: 'agency',
    models_count: '11-50',
    monthly_chat_revenue_range: '',
  })
  const [leadStatus, setLeadStatus] = useState('')

  const preview = useMemo(() => parsePreview(rawConversation, swapRoles), [rawConversation, swapRoles])

  useEffect(() => {
    if (!loading) {
      setElapsedSeconds(0)
      return undefined
    }
    setElapsedSeconds(0)
    const timer = window.setInterval(() => setElapsedSeconds((value) => value + 1), 1000)
    return () => window.clearInterval(timer)
  }, [loading])

  function setMeta(key, value) {
    setMetadata((current) => ({ ...current, [key]: value }))
  }

  function showSampleReport() {
    setRawConversation(SAMPLE_TRANSCRIPT)
    setResult(SAMPLE_RESULT)
    setSampleMode(true)
    setError('')
    window.setTimeout(() => document.querySelector('#sample-report')?.scrollIntoView({ behavior: 'smooth' }), 0)
  }

  async function analyzeConversation(event) {
    event.preventDefault()
    setError('')
    setLeadStatus('')
    setResult(null)
    setSampleMode(false)
    setLoading(true)
    try {
      const response = await fetch(`${API_BASE}/chatter-audit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          raw_conversation: rawConversation,
          metadata: { ...metadata, swap_roles: swapRoles },
        }),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) {
        throw new Error(data.detail || 'Could not analyze this conversation')
      }
      const completed = data.status === 'processing'
        ? await pollAudit(data.audit_id)
        : data
      if (completed.status === 'failed') {
        throw new Error('The audit could not be completed. Please try again.')
      }
      setResult(completed)
    } catch (err) {
      setError(err.message || 'Could not analyze this conversation')
    } finally {
      setLoading(false)
    }
  }

  async function submitLead(event) {
    event.preventDefault()
    if (!result?.audit_id) return
    setLeadStatus('Submitting...')
    try {
      const response = await fetch(`${API_BASE}/chatter-audit/${result.audit_id}/lead`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(lead),
      })
      const data = await response.json().catch(() => ({}))
      if (!response.ok) throw new Error(data.detail || 'Could not submit lead')
      setLeadStatus('Submitted. We can use this audit as the starting point for a team review.')
    } catch (err) {
      setLeadStatus(err.message || 'Could not submit lead')
    }
  }

  return (
    <PublicSiteShell variant="tools">
      <div className="chatter-score-page">
        <section className="cs-hero">
          <div>
            <p className="cs-eyebrow">ChatterScore / Revenue Leak Scanner</p>
            <h1>How good is your chatter, really?</h1>
            <p>
              Paste a real conversation. AI Sales will show what the chatter did right,
              what they missed, and where immediate revenue or future LTV may be leaking.
            </p>
            <div className="cs-hero-actions">
              <a href="#analyze" className="v2-button">Analyze a conversation</a>
              <button type="button" className="cs-secondary-button" onClick={showSampleReport}>View sample report</button>
            </div>
            <span className="cs-no-integration">No integration required</span>
          </div>
          <aside>
            <strong>What this catches</strong>
            <ul>
              <li>Missed buy signals and weak offer timing</li>
              <li>Premature abandonment of high-potential non-buyers</li>
              <li>Over-selling, pressure, poor objection recovery</li>
              <li>Session revenue wins that damage future fan LTV</li>
              <li>Relationship and value signals the chatter ignored</li>
            </ul>
          </aside>
        </section>

        <form id="analyze" className="cs-input-panel" aria-busy={loading} onSubmit={analyzeConversation}>
          <div className="cs-section-head">
            <div>
              <p className="cs-eyebrow">Single conversation test</p>
              <h2>Paste Fan ↔ Chatter conversation</h2>
            </div>
            <button type="button" className="cs-link-button" disabled={loading} onClick={() => setRawConversation(SAMPLE_TRANSCRIPT)}>
              Load sample
            </button>
          </div>

          <textarea
            value={rawConversation}
            onChange={(event) => setRawConversation(event.target.value)}
            placeholder={`Fan: hello\nChatter: hey :)\nFan: ...`}
            rows={13}
            required
            disabled={loading}
          />

          <div className="cs-role-preview">
            <div>
              <span>Detected Fan</span>
              <strong>{preview.fan}</strong>
            </div>
            <div>
              <span>Detected Chatter</span>
              <strong>{preview.chatter}</strong>
            </div>
            <div>
              <span>Confidence</span>
              <strong>{preview.confidence}</strong>
            </div>
            <button type="button" disabled={loading} onClick={() => setSwapRoles((value) => !value)}>
              Swap roles
            </button>
          </div>

          <div className="cs-options-grid">
            <MetadataCheckbox
              checked={metadata.fan_blocked}
              disabled={loading}
              label="Fan was blocked"
              hint="Use only if this is known."
              onChange={(value) => setMeta('fan_blocked', value)}
            />
            <MetadataCheckbox
              checked={metadata.conversation_abandoned}
              disabled={loading}
              label="Conversation was abandoned"
              hint="Chatter intentionally ended or stopped."
              onChange={(value) => setMeta('conversation_abandoned', value)}
            />
            <MetadataCheckbox
              checked={metadata.chatter_stopped_reply}
              disabled={loading}
              label="Chatter stopped replying"
              hint="Helps separate likely vs confirmed abandonment."
              onChange={(value) => setMeta('chatter_stopped_reply', value)}
            />
            <MetadataCheckbox
              checked={metadata.purchase_happened}
              disabled={loading}
              label="Fan made a purchase"
              hint="Optional outcome marker for later QA."
              onChange={(value) => setMeta('purchase_happened', value)}
            />
            <MetadataCheckbox
              checked={metadata.retain_raw_transcript}
              disabled={loading}
              label="Retain raw transcript"
              hint="Off by default. Keep off for public audits."
              onChange={(value) => setMeta('retain_raw_transcript', value)}
            />
          </div>

          <p className="cs-privacy-note">
            Your raw conversation is held only in a short-lived processing cache and sent to the configured
            AI providers for this report. It is not stored in the audit database unless you explicitly enable
            retention; opt-in raw text is deleted automatically after the configured retention window.
            Audit reports also expire automatically. Lead data is stored only if you submit the form.
          </p>

          {error ? <div className="cs-error">{error}</div> : null}
          <button className="v2-button" type="submit" disabled={loading || !rawConversation.trim()}>
            {loading ? 'Analysis running...' : 'Analyze conversation'}
          </button>
          {loading ? <AnalysisProgress elapsedSeconds={elapsedSeconds} /> : null}
        </form>

        {result ? (
          <>
            <ResultPanel result={result} sample={sampleMode} />
            {sampleMode ? (
              <section className="cs-lead-panel cs-sample-cta">
                <div>
                  <p className="cs-eyebrow">Run the same audit on your conversation</p>
                  <h2>Replace the sample with a real transcript.</h2>
                  <p>Your raw transcript remains off by default. Review detected roles, add only known outcome markers, and run the report.</p>
                </div>
                <a className="v2-button" href="#analyze">Analyze my conversation</a>
              </section>
            ) : <section className="cs-lead-panel">
              <div>
                <p className="cs-eyebrow">Want the full revenue audit?</p>
                <h2>Audit 100 conversations</h2>
                <p>
                  We can identify hot-only selling behavior, Future LTV leaks, and
                  high-potential fans your team is abandoning before monetization.
                </p>
              </div>
              <form onSubmit={submitLead}>
                <input
                  type="email"
                  placeholder="Email"
                  value={lead.email}
                  onChange={(event) => setLead((current) => ({ ...current, email: event.target.value }))}
                  required
                />
                <select
                  value={lead.company_type}
                  onChange={(event) => setLead((current) => ({ ...current, company_type: event.target.value }))}
                >
                  <option value="agency">Agency</option>
                  <option value="creator">Creator</option>
                  <option value="chatter_manager">Chatter manager</option>
                  <option value="other">Other</option>
                </select>
                <select
                  value={lead.models_count}
                  onChange={(event) => setLead((current) => ({ ...current, models_count: event.target.value }))}
                >
                  <option value="1">1 model</option>
                  <option value="2-10">2–10 models</option>
                  <option value="11-50">11–50 models</option>
                  <option value="51+">51+ models</option>
                </select>
                <input
                  placeholder="Approx monthly chat revenue"
                  value={lead.monthly_chat_revenue_range}
                  onChange={(event) => setLead((current) => ({ ...current, monthly_chat_revenue_range: event.target.value }))}
                />
                <button className="v2-button" type="submit">Request full audit</button>
                {leadStatus ? <span className="cs-lead-status">{leadStatus}</span> : null}
              </form>
            </section>}
          </>
        ) : null}
      </div>
    </PublicSiteShell>
  )
}
